import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ICON_OPTIONS, COLOR_OPTIONS } from "./collectionOptionConfig";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedIcon: string;
  selectedColor: string;
  onSelect: (icon: string, color: string) => void;
}

export default function IconColorPickerModal({
  open, setOpen, selectedIcon, selectedColor, onSelect,
}: Props) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-base font-semibold text-gray-900">
                    Choose Icon &amp; Colour
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Icon grid */}
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Icon</p>
                <div className="grid grid-cols-5 gap-2 mb-5">
                  {ICON_OPTIONS.map(({ key, label, Component }) => {
                    const active = selectedIcon === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        title={label}
                        onClick={() => onSelect(key, selectedColor)}
                        className={`flex flex-col items-center justify-center gap-1 rounded-lg p-2 border-2 transition-colors ${
                          active
                            ? "border-primary-500 bg-primary-50"
                            : "border-transparent hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <Component className={`h-6 w-6 ${active ? "text-primary-600" : "text-gray-500"}`} />
                        <span className="text-[10px] text-gray-500 leading-tight truncate w-full text-center">
                          {label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Color swatches */}
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Colour</p>
                <div className="grid grid-cols-4 gap-2 mb-5">
                  {COLOR_OPTIONS.map(({ key, label }) => {
                    const active = selectedColor === key;
                    const [bgClass] = key.split(" ");
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => onSelect(selectedIcon, key)}
                        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 border-2 text-sm transition-colors ${
                          active ? "border-primary-500" : "border-transparent hover:border-gray-300"
                        }`}
                      >
                        <span className={`h-4 w-4 rounded-full flex-shrink-0 ${bgClass}`} />
                        <span className="text-xs text-gray-700">{label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Live preview */}
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Preview</p>
                {(() => {
                  const IconComp = ICON_OPTIONS.find((o) => o.key === selectedIcon)?.Component;
                  return (
                    <div className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 ${selectedColor}`}>
                      {IconComp && <IconComp className="h-5 w-5 flex-shrink-0" />}
                      <span className="text-sm font-medium">Collection Option</span>
                    </div>
                  );
                })()}

                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
                  >
                    Done
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
